"use client"

import {
  Activity,
  Bug,
  Goal,
  LayoutDashboard,
  LayoutTemplate,
  Lightbulb,
  Play,
  Plus,
  Route,
  Scale,
  Search,
  Zap
} from "lucide-react"
import * as React from "react"
import {useMemo, useState} from "react"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarTrigger,
  useSidebar
} from "@/components/ui/sidebar"
import {useSession} from "next-auth/react"
import {NavAgent, NavAgentEntry} from "./NavAgent"
import {NavUser} from "./NavUser"
import {CompanySwitcher} from "@/app/components/company/CompanySwitcher";
import {AnimatePresence, motion} from "framer-motion";
import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {useOptionalCompanyId} from "@/hooks/use-company-id";
import {
  bugsPath,
  chatsPath,
  changelogPath,
  dashboardsPath,
  decisionsPath,
  goalsPath,
  ideasPath,
  insightsPath,
  integrationsPath,
  knowledgeFeatureCataloguePath,
  knowledgeSearchPath,
  knowledgePath,
  projectPath,
  projectsPath,
  radarPath,
  quickWinsPath,
  roadmapPath,
  runsPath,
  signalsPath,
  templatesBuilderPath
} from "@/app/paths";
import {TrialStatusCard} from "@/app/components/sidebar/TrialStatusCard";
import {matchPath} from "react-router";
import {usePathname} from "next/navigation";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {gaId} from "@/lib/analytics/clickTracker";
import {subject} from "@casl/ability";
import {CanCompany} from "@/app/components/Can";
import {ProjectEntity} from "@/pb/api/devplan/types/documents";
import {projectKindBug, projectKindIdea, projectKindQuickWin} from "@/app/util/projectsUtil";
import {useCompanyFeatureFlag} from "@/hooks/queries/use-feature-flags";
import {QuickLaunchModal} from "@/app/components/home/QuickLaunchModal";
import {SettingsSidebarNav} from "@/app/components/settings/SettingsSidebarNav";
import {ScopingIntakeDialog} from "@/app/components/scoping/ScopingIntakeDialog";
import {DashboardSidebarNav} from "@/app/components/sidebar/DashboardSidebarNav";
import {ChatSidebarFavorites} from "@/app/components/chat/ChatSidebarFavorites";
import {useSpecsEnabledState} from "@/hooks/use-specs-enabled";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";

export function AppSidebar() {
  const session = useSession()
  const companyId = useOptionalCompanyId()
  const {state} = useSidebar()
  const expanded = state === 'expanded'
  const collapsed = !expanded
  const pathname = usePathname();
  const [quickLaunchOpen, setQuickLaunchOpen] = useState(false);
  const [scopingOpen, setScopingOpen] = useState(false);
  const curProject = useCurrentProject()
  const enableTemplatesBuilder = useCompanyFeatureFlag(ff => ff.wsCustomizableTemplates)
  const enableBugs = useCompanyFeatureFlag(ff => ff.wsBugs)
  const enableWorkspaceMemories = useCompanyFeatureFlag(ff => ff.wsWorkspaceMemories)
  const enableDashboards = useCompanyFeatureFlag(ff => ff.wsDashboards)
  const enableGoals = useCompanyFeatureFlag(ff => ff.wsGoals)
  const enableKnowledgeSearchPage = useCompanyFeatureFlag(ff => ff.wsKnowledgeSearchPage)
  const quickWinPathCheck = useMemo(() => {
    return {
      activeCheck: (path: string) => {
        return curProject?.kind === projectKindQuickWin ||
          (companyId !== undefined && path.startsWith(quickWinsPath(companyId)))
      }
    }
  }, [companyId, curProject?.kind])
  const bugPathCheck = useMemo(() => {
    return {
      activeCheck: (path: string) => {
        return curProject?.kind === projectKindBug ||
          (companyId !== undefined && path.startsWith(bugsPath(companyId)))
      }
    }
  }, [companyId, curProject?.kind])
  const ideasPathCheck = useMemo(() => {
    return {
      activeCheck: (path: string) => {
        return curProject?.kind === projectKindIdea ||
          (companyId !== undefined && path.startsWith(ideasPath(companyId)))
      }
    }
  }, [companyId, curProject?.kind])
  const projectsPathCheck = useMemo(() => {
    return {
      activeCheck: (path: string) => {
        return curProject?.kind !== projectKindQuickWin && curProject?.kind !== projectKindIdea &&
          curProject?.kind !== projectKindBug &&
          (companyId !== undefined && path.startsWith(projectsPath(companyId)) && !path.startsWith(roadmapPath(companyId)))
      }
    }
  }, [companyId, curProject?.kind])
  const decisionsPathCheck = useMemo(() => {
    return {
      activeCheck: (path: string) => {
        return companyId !== undefined && path.startsWith(decisionsPath(companyId))
      }
    }
  }, [companyId])
  const dashboardsPathCheck = useMemo(() => {
    return {
      activeCheck: (path: string) => {
        return companyId !== undefined && path.startsWith(dashboardsPath(companyId))
      }
    }
  }, [companyId])
  const {specsEnabled, loading: specsLoading} = useSpecsEnabledState(companyId)
  const sidebarDataLoaded = !specsLoading

  const roadmapPathCheck = useMemo(() => {
    return {
      activeCheck: (path: string) => {
        return companyId !== undefined && path.startsWith(roadmapPath(companyId))
      }
    }
  }, [companyId])
  if (!session.data?.user) {
    return null
  }

  const pathWithCheck = (url: string) => {
    return {url, activeCheck: (path: string) => path.startsWith(url)}
  }

  const navAgent: NavAgentEntry[] = companyId ? [
    {
      title: "Weaver",
      icon: AskDevplanIcon,
      className: "size-8",
      ...pathWithCheck(chatsPath(companyId)),
    },
    {
      title: "Radar",
      icon: Activity,
      className: "size-8",
      ...pathWithCheck(radarPath(companyId)),
    },
    ...(enableKnowledgeSearchPage ? [{
      title: "Search KB",
      icon: Search,
      className: "size-8",
      ...pathWithCheck(knowledgeSearchPath(companyId)),
    }] : []),
    ...(enableGoals ? [{
      title: "Goals",
      icon: Goal,
      className: "size-8",
      ...pathWithCheck(goalsPath(companyId)),
    }] : []),
    {
      title: "Projects",
      icon: PrototypeFolderIcon,
      className: "size-8",
      url: projectsPath(companyId),
      ...projectsPathCheck,
    },
    {
      title: "Insights",
      icon: PrototypeTargetIcon,
      className: "size-8",
      ...pathWithCheck(insightsPath(companyId)),
    },
    {
      title: "Changelog",
      icon: PrototypeInboxIcon,
      className: "size-8",
      ...pathWithCheck(changelogPath(companyId)),
    },
    ...(enableDashboards ? [{
      title: "Dashboards",
      icon: LayoutDashboard,
      className: "size-8",
      url: dashboardsPath(companyId),
      ...dashboardsPathCheck,
    }] : []),
    ...(enableWorkspaceMemories ? [{
      title: "Decisions",
      icon: Scale,
      className: "size-8",
      url: decisionsPath(companyId),
      ...decisionsPathCheck,
    }] : []),
    ...(specsEnabled ? [{
      title: "Specs",
      items: [
        {
          title: "Roadmap",
          icon: Route,
          className: "size-8",
          url: roadmapPath(companyId),
          ...roadmapPathCheck,
        },
        {
          title: "Ideas",
          icon: Lightbulb,
          className: "size-8",
          url: ideasPath(companyId),
          ...ideasPathCheck,
        },
        {
          title: "Features",
          icon: Zap,
          className: "size-8",
          url: quickWinsPath(companyId),
          ...quickWinPathCheck,
        },
        ...(enableBugs ? [{
          title: "Bugs",
          icon: Bug,
          className: "size-8",
          url: bugsPath(companyId),
          ...bugPathCheck,
        }] : []),
        {
          title: "Runs",
          icon: Play,
          className: "size-8",
          ...pathWithCheck(runsPath(companyId)),
        },
      ],
    }] : []),
    {
      title: "Knowledge",
      items: [
        {
          title: "Workspace",
          icon: PrototypeBuildingIcon,
          className: "size-8",
          ...pathWithCheck(knowledgePath(companyId)),
        },
        {
          title: "Product",
          icon: PrototypeBookIcon,
          className: "size-8",
          ...pathWithCheck(knowledgeFeatureCataloguePath(companyId)),
        },
        {
          title: "Signals",
          icon: PrototypeStackLayerIcon,
          className: "size-8",
          ...pathWithCheck(signalsPath(companyId)),
        },
        {
          title: "Integrations",
          icon: PrototypeLinkIcon,
          className: "size-8",
          ...pathWithCheck(integrationsPath(companyId)),
        },
        ...(specsEnabled && enableTemplatesBuilder ? [{
          title: "Templates",
          icon: LayoutTemplate,
          className: "size-8",
          ...pathWithCheck(templatesBuilderPath(companyId)),
        }] : []),
      ],
    },
  ] : []
  const settingsMode = pathname.startsWith("/settings")

  return <Sidebar collapsible="icon" variant="sidebar">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="w-full">
            <div className="flex w-full items-center justify-between h-[40px]">
              <AnimatePresence>
                {expanded && <motion.div
                  exit={{opacity: 0}}
                  transition={{duration: 0, delay: 0.15}}
                  className="flex items-center overflow-hidden"
                >
                  <CompanySwitcher/>
                </motion.div>}
              </AnimatePresence>
              <SidebarTrigger/>
            </div>
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
      {companyId && !settingsMode && !sidebarDataLoaded && (
        <SidebarMenu>
          <SidebarMenuSkeleton showIcon/>
        </SidebarMenu>
      )}
      {companyId && !settingsMode && sidebarDataLoaded && (
        <SidebarMenu>
          <CanCompany do="create" on={subject('Project', {companyId})} blurForLimits>
            <SidebarMenuItem>
              <div className="w-full">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      className="flex items-center justify-center w-full"
                      size="sm"
                      {...gaId("new_project_button_sidebar")}
                      onClick={() => {
                        if (specsEnabled) {
                          setQuickLaunchOpen(true)
                        } else {
                          setScopingOpen(true)
                        }
                      }}
                    >
                      <Plus className={cn(collapsed && "ml-1.5")}/>
                      <span className="truncate">Add</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="center" hidden={!collapsed}>
                    Add
                  </TooltipContent>
                </Tooltip>
              </div>
            </SidebarMenuItem>
          </CanCompany>
        </SidebarMenu>
      )}
    </SidebarHeader>

    <SidebarContent className="">
      {!sidebarDataLoaded ? (
        <SidebarLoadingSkeleton/>
      ) : settingsMode ? (
        <SettingsSidebarNav/>
      ) : companyId && (
        <div className="space-y-4">
          <NavAgent
            items={navAgent}
            subContent={{
              Weaver: <ChatSidebarFavorites companyId={companyId}/>,
              ...(enableDashboards ? {Dashboards: <DashboardSidebarNav companyId={companyId}/>} : {}),
            }}
          />
        </div>
      )}
    </SidebarContent>

    <SidebarFooter className={cn(collapsed && "px-0")}>
      <SidebarGroup>
        <TrialStatusCard/>
      </SidebarGroup>
      <SidebarGroup>
        <NavUser/>
      </SidebarGroup>
    </SidebarFooter>
    {companyId && (
      <>
        <QuickLaunchModal
          open={quickLaunchOpen}
          onOpenChange={setQuickLaunchOpen}
        />
        <ScopingIntakeDialog
          open={scopingOpen}
          onOpenChange={setScopingOpen}
          companyId={companyId}
        />
      </>
    )}
  </Sidebar>
}

function SidebarLoadingSkeleton() {
  return (
    <SidebarGroup className="px-2 py-0">
      <SidebarMenu>
        <SidebarMenuSkeleton showIcon/>
        <SidebarMenuSkeleton showIcon/>
        <SidebarMenuSkeleton showIcon/>
        <SidebarMenuSkeleton showIcon/>
        <SidebarMenuSkeleton showIcon/>
        <SidebarMenuItem className="pt-[5px]">
          <SidebarMenuSkeleton className="h-7" />
        </SidebarMenuItem>
        <SidebarMenuSkeleton showIcon/>
        <SidebarMenuSkeleton showIcon/>
        <SidebarMenuSkeleton showIcon/>
      </SidebarMenu>
    </SidebarGroup>
  )
}

function PrototypeSvg({children, ...props}: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  )
}

function AskDevplanIcon(props: React.SVGProps<SVGSVGElement>) {
  const size = 16
  const inset = size * 0.16
  const step = (size - inset * 2) / 3
  const r = Math.max(0.6, size * 0.06)
  const dots = []
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      dots.push(
        <circle
          key={`${row}-${col}`}
          cx={inset + col * step}
          cy={inset + row * step}
          r={r}
          style={{"--ask-d": `${Math.round((col / 3) * 800)}ms`} as React.CSSProperties}
        />
      )
    }
  }
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      {...props}
      className={cn("ask-dotgrid", props.className)}
      style={{transform: "rotate(45deg)", transformOrigin: "center", overflow: "visible", ...props.style}}
    >
      {dots}
    </svg>
  )
}

function PrototypeFolderIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <PrototypeSvg {...props}>
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z"/>
    </PrototypeSvg>
  )
}

function PrototypeInboxIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <PrototypeSvg {...props}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    </PrototypeSvg>
  )
}

function PrototypeTargetIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <PrototypeSvg {...props}>
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </PrototypeSvg>
  )
}

function PrototypeBuildingIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <PrototypeSvg {...props}>
      <rect width="16" height="20" x="4" y="2" rx="2"/>
      <path d="M9 22v-4h6v4"/>
      <path d="M8 6h.01"/>
      <path d="M16 6h.01"/>
      <path d="M12 6h.01"/>
      <path d="M12 10h.01"/>
      <path d="M12 14h.01"/>
      <path d="M16 10h.01"/>
      <path d="M16 14h.01"/>
      <path d="M8 10h.01"/>
      <path d="M8 14h.01"/>
    </PrototypeSvg>
  )
}

function PrototypeBookIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <PrototypeSvg {...props}>
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v17a1 1 0 0 1-1 1H6.5a2.5 2.5 0 0 1 0-5H20"/>
    </PrototypeSvg>
  )
}

function PrototypeStackLayerIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <PrototypeSvg {...props}>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/>
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/>
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/>
    </PrototypeSvg>
  )
}

function PrototypeLinkIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <PrototypeSvg {...props}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </PrototypeSvg>
  )
}

function useCurrentProject(): ProjectEntity | undefined {
  const path = usePathname();
  const match = useMemo(() => matchPath(`${projectPath(":companyId", ":projectId")}/*`, path), [path]);
  const projectId = match?.params.projectId;
  return useBoundStore(s => {
    if (!projectId) {
      return undefined
    }
    return s.projects[projectId]
  })
}
